import React, { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Trash2, FileText, Upload, Wallet, Save, ShieldOff } from "lucide-react";

export default function ChefCSE() {
  const [cse, setCse] = useState(null);
  const [error, setError] = useState(null);
  const [budget, setBudget] = useState("");
  const [notes, setNotes] = useState("");
  const [elus, setElus] = useState([]);
  const [eluForm, setEluForm] = useState({ nom: "", prenom: "", fonction: "" });
  const [eluOpen, setEluOpen] = useState(false);
  const [docTitle, setDocTitle] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/chef/cse");
      setCse(data);
      setBudget(String(data.budget_annuel || 0));
      setNotes(data.notes || "");
      setElus(data.elus || []);
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      setError(detail || "Module non disponible");
    }
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    try {
      await api.post("/chef/cse", {
        budget_annuel: parseFloat(budget) || 0,
        notes,
        elus,
      });
      toast.success("Configuration CSE enregistrée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addElu = (e) => {
    e.preventDefault();
    setElus([...elus, eluForm]);
    setEluForm({ nom: "", prenom: "", fonction: "" });
    setEluOpen(false);
  };
  const removeElu = (idx) => setElus(elus.filter((_, i) => i !== idx));

  const uploadDoc = async (e) => {
    e.preventDefault();
    if (!file || !docTitle) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/chef/cse/document?title=${encodeURIComponent(docTitle)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté");
      setDocTitle(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try { await api.delete(`/chef/cse/document/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await api.get(`/files/cse/${doc.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = doc.filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (error) {
    return (
      <div className="min-h-full">
        <PageHeader eyebrow="CSE / CE" title="Comité Social et Économique" sub="Module avancé pour gérer le CSE de votre entreprise." />
        <div className="p-6 lg:p-10">
          <div className="bg-white border border-[#DEE2E6] p-10 text-center max-w-2xl" data-testid="cse-disabled">
            <ShieldOff className="h-12 w-12 mx-auto mb-4 text-[#DEE2E6]" />
            <h3 className="font-display text-xl font-bold mb-2">Module CSE non activé</h3>
            <p className="text-sm text-[#495057]">Ce module doit être activé par l'administrateur PAG pour votre entreprise. Contactez le support PAG Helper pour faire la demande.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="CSE / CE"
        title="Comité Social et Économique"
        sub="Pilotez votre CSE : budget, élus, comptes-rendus."
        right={<Button onClick={saveConfig} data-testid="cse-save-btn" className="h-11 bg-[#002FA7] text-white"><Save className="h-4 w-4 mr-2" /> Enregistrer</Button>}
      />
      <div className="p-6 lg:p-10 space-y-6">
        {/* Budget */}
        <div className="bg-white border border-[#DEE2E6] p-6" data-testid="cse-budget-section">
          <div className="text-xs uppercase tracking-[0.25em] font-bold mb-3 flex items-center gap-2"><Wallet className="h-4 w-4 text-[#002FA7]" /> Budget annuel</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Montant annuel (€)</Label>
              <Input type="number" step="0.01" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="cse-budget" className="h-11 mt-1.5 font-mono text-lg font-bold" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Notes / observations</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="cse-notes" className="mt-1.5" placeholder="Notes internes…" />
            </div>
          </div>
        </div>

        {/* Élus */}
        <div className="bg-white border border-[#DEE2E6] p-6" data-testid="cse-elus-section">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.25em] font-bold flex items-center gap-2"><Users className="h-4 w-4 text-[#002FA7]" /> Élus du CSE ({elus.length})</div>
            <Dialog open={eluOpen} onOpenChange={setEluOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="cse-elu-add-btn" className="h-9 bg-[#002FA7] text-white"><Plus className="h-3 w-3 mr-1" /> Ajouter un élu</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Nouvel élu</DialogTitle></DialogHeader>
                <form onSubmit={addElu} className="space-y-3" data-testid="cse-elu-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Nom</Label>
                      <Input required value={eluForm.nom} onChange={(e) => setEluForm({ ...eluForm, nom: e.target.value })} data-testid="cse-elu-nom" className="h-11" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Prénom</Label>
                      <Input required value={eluForm.prenom} onChange={(e) => setEluForm({ ...eluForm, prenom: e.target.value })} data-testid="cse-elu-prenom" className="h-11" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Fonction</Label>
                    <Input required value={eluForm.fonction} onChange={(e) => setEluForm({ ...eluForm, fonction: e.target.value })} data-testid="cse-elu-fonction" placeholder="Titulaire, suppléant, secrétaire…" className="h-11" />
                  </div>
                  <DialogFooter><Button type="submit" data-testid="cse-elu-submit" className="bg-[#002FA7] text-white">Ajouter</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {elus.length === 0 ? (
            <div className="text-sm text-[#868E96] italic">Aucun élu défini. Cliquez sur "Ajouter un élu".</div>
          ) : (
            <div className="space-y-2">
              {elus.map((e, i) => (
                <div key={i} className="grid grid-cols-12 gap-3 items-center p-3 bg-[#F8F9FA] border border-[#DEE2E6]" data-testid={`cse-elu-${i}`}>
                  <div className="col-span-4 font-bold">{e.prenom} {e.nom}</div>
                  <div className="col-span-6 text-sm text-[#495057]">{e.fonction}</div>
                  <div className="col-span-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => removeElu(i)} className="h-7 border-[#E60000] text-[#E60000]"><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Button onClick={saveConfig} data-testid="cse-save-elus-btn" className="mt-4 w-full bg-[#0A0A0A] text-white h-10">
            <Save className="h-4 w-4 mr-2" /> Enregistrer la liste des élus
          </Button>
        </div>

        {/* Documents */}
        <div className="bg-white border border-[#DEE2E6] p-6" data-testid="cse-docs-section">
          <div className="text-xs uppercase tracking-[0.25em] font-bold mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-[#002FA7]" /> Comptes-rendus & documents</div>
          <form onSubmit={uploadDoc} className="grid md:grid-cols-12 gap-3 mb-4 p-3 bg-[#F8F9FA] border border-[#DEE2E6]" data-testid="cse-doc-form">
            <Input className="md:col-span-5 h-10" placeholder="Titre du document" value={docTitle} onChange={(e) => setDocTitle(e.target.value)} required data-testid="cse-doc-title" />
            <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)} className="md:col-span-5 h-10 px-3 py-2 border border-[#DEE2E6] bg-white text-sm" required data-testid="cse-doc-file" />
            <Button type="submit" disabled={uploading || !file || !docTitle} data-testid="cse-doc-upload" className="md:col-span-2 h-10 bg-[#002FA7] text-white"><Upload className="h-4 w-4 mr-2" /> {uploading ? "Envoi…" : "Ajouter"}</Button>
          </form>
          {(cse?.documents || []).length === 0 ? (
            <div className="text-sm text-[#868E96] italic">Aucun document ajouté.</div>
          ) : (
            <div className="space-y-2">
              {(cse.documents || []).map((d) => (
                <div key={d.id} className="grid grid-cols-12 gap-3 items-center p-3 border border-[#DEE2E6] hover:bg-[#F8F9FA]" data-testid={`cse-doc-${d.id}`}>
                  <div className="col-span-1"><FileText className="h-4 w-4 text-[#002FA7]" /></div>
                  <div className="col-span-7">
                    <div className="font-semibold text-sm">{d.title}</div>
                    <div className="text-[10px] text-[#868E96] font-mono">{d.filename} · {new Date(d.uploaded_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                  <div className="col-span-4 flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => downloadDoc(d)} data-testid={`cse-doc-download-${d.id}`}>Télécharger</Button>
                    <Button size="sm" variant="outline" onClick={() => removeDoc(d.id)} data-testid={`cse-doc-delete-${d.id}`} className="border-[#E60000] text-[#E60000]"><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
